import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Fab, Paper, Typography, TextField, IconButton, Avatar,
  Chip, Divider, Tooltip, Badge, CircularProgress, Card, CardContent,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Send as SendIcon,
  Close as CloseIcon,
  AutoAwesome as SparkleIcon,
  Minimize as MinimizeIcon,
  OpenInFull as ExpandIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';

// ─────────────────────────────────────────────────────────────
// INTENT DEFINITIONS  (all pages + actions + data queries)
// ─────────────────────────────────────────────────────────────
const INTENTS = [
  // ── General / Greeting ──────────────────────────────────────
  {
    patterns: [/^(hi|hello|hey|مرحبا|أهلا|السلام|هلا|هاي|صباح|مساء|كيف حالك)/i],
    respond: () => ({ text: 'مرحباً! كيف يمكنني مساعدتك اليوم؟', suggestions: ['إحصائيات سريعة', 'قائمة الطلاب', 'الصفحة المالية', 'إضافة طالب جديد'] }),
  },
  {
    patterns: [/help|مساعدة|ماذا تستطيع|ما الذي تفعله|ماذا تعرف/i],
    respond: () => ({
      text: 'يمكنني مساعدتك في:\n• التنقل بين الصفحات\n• عرض إحصائيات سريعة\n• الإجراءات السريعة (إضافة طالب، موظف...)\n• البحث والاستفسار عن بيانات النظام\n\nما الذي تريد فعله؟',
      suggestions: ['إحصائيات النظام', 'إضافة طالب', 'إضافة موظف', 'التقارير'],
    }),
  },
  {
    patterns: [/شكر|شكراً|شكرا|تسلم|ممتاز|عظيم|رائع|thank|great/i],
    respond: () => ({ text: 'بكل سرور! هل هناك شيء آخر أقدر أساعدك به؟', suggestions: ['الرئيسية', 'إحصائيات', 'إضافة بيانات'] }),
  },

  // ── Dashboard ───────────────────────────────────────────────
  {
    patterns: [/dashboard|لوحة.*تحكم|الرئيسية|الصفحة الرئيسية/i],
    respond: () => ({ text: 'سأنقلك إلى الواجهة الرئيسية.', action: 'navigate', target: '/dashboard' }),
  },
  {
    patterns: [/teacher.*dashboard|لوحة.*معلم|داشبورد المعلم/i],
    respond: () => ({ text: 'سأنقلك إلى لوحة تحكم المعلم.', action: 'navigate', target: '/teacher-dashboard' }),
  },

  // ── Students ─────────────────────────────────────────────────
  {
    patterns: [/^(student|طالب|طلاب|تلميذ|تلاميذ|قائمة الطلاب|عرض الطلاب)$/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة الطلاب.', action: 'navigate', target: '/students' }),
  },
  {
    patterns: [/add student|create student|new student|إضافة طالب|طالب جديد|تسجيل طالب|أضف طالب/i],
    respond: () => ({ text: 'سأنقلك لتسجيل طالب جديد.', action: 'navigate', target: '/students/create' }),
  },
  {
    patterns: [/archive.*student|أرشيف.*طالب|طلاب.*أرشيف|archived students/i],
    respond: () => ({ text: 'سأنقلك إلى أرشيف الطلاب.', action: 'navigate', target: '/archivestudents' }),
  },
  {
    patterns: [/student.*stat|إحصاء.*طالب|عدد.*طالب|كم.*طالب|how many student/i],
    respond: () => ({ text: 'دعني أجلب إحصائيات الطلاب...', action: 'fetch', endpoint: '/api/students/dashboard/quick-stats/' }),
  },
  {
    patterns: [/unpaid|غير.*مدفوع|طلاب.*متأخر|متأخرين|students.*unpaid/i],
    respond: () => ({ text: 'سأعرض لك الطلاب الذين لديهم مبالغ غير مدفوعة.', action: 'navigate', target: '/students?filter=unpaid' }),
  },

  // ── Staff / Employees ────────────────────────────────────────
  {
    patterns: [/^(staff|موظف|موظفين|الموظفون|قائمة الموظفين|عرض الموظفين)$/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة الموظفين.', action: 'navigate', target: '/staff' }),
  },
  {
    patterns: [/add staff|add employee|create staff|new employee|إضافة موظف|موظف جديد|أضف موظف/i],
    respond: () => ({ text: 'سأنقلك لإضافة موظف جديد.', action: 'navigate', target: '/staff/create' }),
  },
  {
    patterns: [/archive.*staff|أرشيف.*موظف|موظفين.*أرشيف/i],
    respond: () => ({ text: 'سأنقلك إلى أرشيف الموظفين.', action: 'navigate', target: '/archivestaff' }),
  },
  {
    patterns: [/staff.*stat|إحصاء.*موظف|عدد.*موظف|كم.*موظف|teacher.*absence|غياب.*معلم/i],
    respond: () => ({ text: 'دعني أجلب إحصائيات الموظفين...', action: 'fetch', endpoint: '/api/employees/dashboard-stats/' }),
  },
  {
    patterns: [/substitute|مناوب|مناوبون|بديل|معلم بديل/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة المناوبين.', action: 'navigate', target: '/substitute-teachers' }),
  },

  // ── Financial ─────────────────────────────────────────────────
  {
    patterns: [/^(financial|مالي|مالية|إدارة مالية|الصفحة المالية)$/i],
    respond: () => ({ text: 'سأنقلك إلى إدارة سندات الصرف.', action: 'navigate', target: '/financial' }),
  },
  {
    patterns: [/payment.*stat|إحصاء.*مالي|إحصائيات.*مالية|finance.*stat|ملخص مالي/i],
    respond: () => ({ text: 'دعني أجلب الإحصائيات المالية...', action: 'fetch', endpoint: '/api/payments/dashboard-stats/' }),
  },
  {
    patterns: [/سند.*صرف|إصدار سند|صرف|outgoing voucher|payment voucher/i],
    respond: () => ({ text: 'سأنقلك إلى إدارة سندات الصرف.', action: 'navigate', target: '/financial' }),
  },
  {
    patterns: [/سند.*قبض|receipt|قبض/i],
    respond: () => ({ text: 'سأنقلك إلى إدارة سندات القبض.', action: 'navigate', target: '/recipient' }),
  },
  {
    patterns: [/cheque.*status|وضع.*شيك|دفعات.*طلاب|student.*payment/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة دفعات الطلاب.', action: 'navigate', target: '/finance/cheques-status' }),
  },
  {
    patterns: [/outgoing.*cheque|شيك.*صادر|دفعات.*مدرسة|school payment/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة دفعات المدرسة.', action: 'navigate', target: '/finance/outgoingcheque' }),
  },
  {
    patterns: [/fee.*management|إدارة.*رسوم|رسوم دراسية|school fees/i],
    respond: () => ({ text: 'سأنقلك إلى إدارة الرسوم الدراسية.', action: 'navigate', target: '/fee-management' }),
  },
  {
    patterns: [/add.*payment|create.*payment|إضافة دفعة|دفعة جديدة|سند جديد/i],
    respond: () => ({ text: 'سأنقلك لإضافة سند دفع جديد.', action: 'navigate', target: '/finance/create' }),
  },
  {
    patterns: [/authorized.*payer|جهة.*صرف|جهات.*صرف|معتمد/i],
    respond: () => ({ text: 'سأنقلك إلى الموردون.', action: 'navigate', target: '/authorized-payers' }),
  },

  // ── Classes / Academic ────────────────────────────────────────
  {
    patterns: [/^(class|classes|صف|صفوف|فصل|شعبة|قائمة الصفوف)$/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة الصفوف.', action: 'navigate', target: '/classes' }),
  },
  {
    patterns: [/add.*class|create.*class|إضافة صف|صف جديد/i],
    respond: () => ({ text: 'سأنقلك لإضافة صف دراسي جديد.', action: 'navigate', target: '/classes/create' }),
  },
  {
    patterns: [/subject|مادة|مواد|مناهج|curriculum/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة المواد الدراسية.', action: 'navigate', target: '/subjects' }),
  },
  {
    patterns: [/schedule|جدول.*دراسي|جدول.*تعليمي|educational.*schedule|دوام|حصص/i],
    respond: () => ({ text: 'سأنقلك إلى الجدول الدراسي.', action: 'navigate', target: '/educational-schedule' }),
  },
  {
    patterns: [/academic.*planning|تخطيط أكاديمي|التخطيط الأكاديمي/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة التخطيط الأكاديمي.', action: 'navigate', target: '/academic-planning' }),
  },

  // ── Buses ────────────────────────────────────────────────────
  {
    patterns: [/bus|buses|باص|حافلة|حافلات|باصات/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة الحافلات.', action: 'navigate', target: '/buses' }),
  },
  {
    patterns: [/add.*bus|new bus|إضافة حافلة|حافلة جديدة/i],
    respond: () => ({ text: 'سأنقلك لإضافة حافلة جديدة.', action: 'navigate', target: '/buses/create' }),
  },

  // ── Store / Inventory ─────────────────────────────────────────
  {
    patterns: [/^(store|مستودع|المستودع|inventory|مخزن)$/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة المستودع.', action: 'navigate', target: '/store' }),
  },
  {
    patterns: [/store.*purchase|مبيعات|مشتريات.*مدرسة/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة مبيعات المدرسة.', action: 'navigate', target: '/store-purchases' }),
  },
  {
    patterns: [/miscellaneous|متفرقات|منوعات/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة المتفرقات.', action: 'navigate', target: '/miscellaneous' }),
  },

  // ── Library ───────────────────────────────────────────────────
  {
    patterns: [/library|مكتبة|كتب|كتاب/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة المكتبة.', action: 'navigate', target: '/library' }),
  },
  {
    patterns: [/library.*stat|إحصاء.*مكتبة|ملخص.*مكتبة/i],
    respond: () => ({ text: 'دعني أجلب إحصائيات المكتبة...', action: 'fetch', endpoint: '/api/library/dashboard/' }),
  },

  // ── Events ────────────────────────────────────────────────────
  {
    patterns: [/event|events|فعالية|فعاليات|حدث|نشاط/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة الفعاليات.', action: 'navigate', target: '/events' }),
  },

  // ── Admin ─────────────────────────────────────────────────────
  {
    patterns: [/reports|تقرير|تقارير/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة التقارير.', action: 'navigate', target: '/reports' }),
  },
  {
    patterns: [/user.*management|إدارة.*مستخدم|مستخدمين/i],
    respond: () => ({ text: 'سأنقلك إلى إدارة المستخدمين.', action: 'navigate', target: '/admin/users' }),
  },
  {
    patterns: [/logs|سجل.*تغيير|سجلات|audit/i],
    respond: () => ({ text: 'سأنقلك إلى سجل التغييرات.', action: 'navigate', target: '/admin/logs' }),
  },
  {
    patterns: [/year.*transition|انتقال.*سنة|تخرج|ترفيع|year end/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة انتقال السنة الدراسية.', action: 'navigate', target: '/admin/year-transition' }),
  },
  {
    patterns: [/setting|إعداد|إعدادات|system config/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة الإعدادات.', action: 'navigate', target: '/settings' }),
  },
  {
    patterns: [/reset.*password|تغيير.*كلمة.*مرور|كلمة مرور جديدة/i],
    respond: () => ({ text: 'سأنقلك إلى صفحة إعادة تعيين كلمة المرور.', action: 'navigate', target: '/reset-password' }),
  },

  // ── Compound stats ────────────────────────────────────────────
  {
    patterns: [/إحصائيات|stats|statistics|ملخص|overview|نظرة عامة|summary/i],
    respond: () => ({ text: 'دعني أجلب إحصائيات شاملة للنظام...', action: 'fetch_all' }),
  },
];

// ─────────────────────────────────────────────────────────────
// QUICK ACTIONS  (grid of chips at the top)
// ─────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'الطلاب',       target: '/students',             icon: '🎓' },
  { label: 'الموظفين',     target: '/staff',                icon: '👤' },
  { label: 'المالية',      target: '/financial',            icon: '💰' },
  { label: 'الصفوف',       target: '/classes',              icon: '🏫' },
  { label: 'إضافة طالب',  target: '/students/create',      icon: '➕' },
  { label: 'الجدول',       target: '/educational-schedule', icon: '📅' },
  { label: 'الحافلات',    target: '/buses',                 icon: '🚌' },
  { label: 'المكتبة',      target: '/library',              icon: '📚' },
  { label: 'الفعاليات',   target: '/events',               icon: '🎉' },
  { label: 'التقارير',     target: '/reports',              icon: '📊' },
  { label: 'الإعدادات',   target: '/settings',             icon: '⚙️' },
  { label: 'الأرشيف',     target: '/archivestudents',       icon: '📦' },
];

// ─────────────────────────────────────────────────────────────
// PAGE-CONTEXT SUGGESTIONS  (shown when user is on a specific page)
// ─────────────────────────────────────────────────────────────
const PAGE_CONTEXT = {
  '/students':              { label: 'الطلاب',       suggestions: ['إضافة طالب جديد', 'إحصائيات الطلاب', 'أرشيف الطلاب', 'دفعات الطلاب'] },
  '/staff':                 { label: 'الموظفين',     suggestions: ['إضافة موظف جديد', 'إحصائيات الموظفين', 'المناوبون', 'أرشيف الموظفين'] },
  '/financial':             { label: 'المالية',       suggestions: ['إضافة سند جديد', 'إحصائيات مالية', 'دفعات الطلاب', 'جهات الصرف'] },
  '/classes':               { label: 'الصفوف',       suggestions: ['إضافة صف جديد', 'المواد الدراسية', 'الجدول الدراسي'] },
  '/library':               { label: 'المكتبة',      suggestions: ['إحصائيات المكتبة', 'التقارير'] },
  '/events':                { label: 'الفعاليات',    suggestions: ['الجدول الدراسي', 'الطلاب'] },
  '/reports':               { label: 'التقارير',     suggestions: ['إحصائيات سريعة', 'سجل التغييرات'] },
  '/admin/year-transition': { label: 'انتقال السنة', suggestions: ['معاينة الترفيع', 'إحصائيات الطلاب'] },
  '/buses':                 { label: 'الحافلات',     suggestions: ['إضافة حافلة جديدة', 'الطلاب'] },
  '/store':                 { label: 'المستودع',     suggestions: ['مبيعات المدرسة', 'المتفرقات'] },
  '/substitute-teachers':   { label: 'المناوبون',    suggestions: ['الموظفين', 'الجدول الدراسي'] },
};

// ─────────────────────────────────────────────────────────────
// INTENT DETECTION
// ─────────────────────────────────────────────────────────────
function detectIntent(text) {
  for (const intent of INTENTS) {
    if (intent.patterns.some((p) => p.test(text))) {
      return intent.respond();
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// FORMAT STATS RESPONSE  (pretty-print API data inline)
// ─────────────────────────────────────────────────────────────
function formatStats(endpoint, data) {
  if (!data) return 'لم يتم العثور على بيانات.';

  if (endpoint.includes('quick-stats') || endpoint.includes('students')) {
    return {
      title: 'إحصائيات الطلاب',
      rows: [
        { label: 'إجمالي الطلاب',     value: data.total_students ?? data.total ?? '—' },
        { label: 'طلاب نشطون',        value: data.active_students ?? data.active ?? '—' },
        { label: 'طلاب مؤرشفون',     value: data.archived_students ?? data.archived ?? '—' },
        { label: 'غير مسددين',        value: data.unpaid_students ?? data.unpaid ?? '—' },
      ],
    };
  }
  if (endpoint.includes('employees') || endpoint.includes('dashboard-stats') && endpoint.includes('employee')) {
    return {
      title: 'إحصائيات الموظفين',
      rows: [
        { label: 'إجمالي الموظفين',  value: data.total_employees ?? data.total ?? '—' },
        { label: 'موظفون نشطون',     value: data.active_employees ?? data.active ?? '—' },
        { label: 'غيابات اليوم',    value: data.absences_today ?? data.today_absences ?? '—' },
      ],
    };
  }
  if (endpoint.includes('payments') || endpoint.includes('payment')) {
    return {
      title: 'الإحصائيات المالية',
      rows: [
        { label: 'إجمالي سندات الصرف',  value: data.total_payments ?? data.total ?? '—' },
        { label: 'مسدد',               value: data.paid ?? data.total_paid ?? '—' },
        { label: 'غير مسدد',           value: data.unpaid ?? data.total_unpaid ?? '—' },
        { label: 'شيكات معلقة',        value: data.pending_cheques ?? data.pending ?? '—' },
      ],
    };
  }
  if (endpoint.includes('library')) {
    return {
      title: 'إحصائيات المكتبة',
      rows: [
        { label: 'إجمالي الكتب',     value: data.total_books ?? data.books ?? '—' },
        { label: 'كتب مستعارة',      value: data.borrowed_books ?? data.borrowed ?? '—' },
        { label: 'متأخرة في الإعادة', value: data.overdue_books ?? data.overdue ?? '—' },
      ],
    };
  }
  // fallback: show raw key-value
  return {
    title: 'البيانات',
    rows: Object.entries(data).slice(0, 6).map(([k, v]) => ({ label: k, value: String(v) })),
  };
}

// ─────────────────────────────────────────────────────────────
// MESSAGE SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────
function BotMessage({ msg, onSuggestion, onNavigate }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: '#6c63ff', fontSize: 14, flexShrink: 0, mt: 0.3 }}>
        <BotIcon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box sx={{ maxWidth: '82%' }}>
        <Paper elevation={0} sx={{ px: 1.5, py: 1, bgcolor: '#f0edff', borderRadius: '4px 14px 14px 14px', border: '1px solid #e0d9ff' }}>
          <Typography variant="body2" sx={{ color: '#2d2560', lineHeight: 1.7, whiteSpace: 'pre-line', fontSize: 12.5 }}>
            {msg.text}
          </Typography>
        </Paper>

        {/* Stats card */}
        {msg.stats && (
          <Card elevation={1} sx={{ mt: 0.75, borderRadius: 2, border: '1px solid #e0d9ff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#6c63ff' }}>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>{msg.stats.title}</Typography>
            </Box>
            <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
              {msg.stats.rows.map((r) => (
                <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>{r.label}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#2d2560' }}>{r.value}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Navigate button */}
        {msg.action === 'navigate' && msg.target && (
          <Chip
            size="small"
            label="انتقل الآن ←"
            onClick={() => onNavigate(msg.target)}
            sx={{ mt: 0.5, bgcolor: '#6c63ff', color: '#fff', cursor: 'pointer', fontSize: 11, '&:hover': { bgcolor: '#5a52cc' } }}
          />
        )}

        {/* Suggestion chips */}
        {msg.suggestions?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {msg.suggestions.map((s) => (
              <Chip key={s} label={s} size="small" variant="outlined" onClick={() => onSuggestion(s)}
                sx={{ cursor: 'pointer', fontSize: 11, borderColor: '#6c63ff', color: '#6c63ff', '&:hover': { bgcolor: '#f0edff' } }} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function UserMessage({ msg }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
      <Paper elevation={0} sx={{ px: 1.5, py: 1, bgcolor: '#6c63ff', borderRadius: '14px 4px 14px 14px', maxWidth: '75%' }}>
        <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.6, fontSize: 12.5 }}>{msg.text}</Typography>
      </Paper>
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: '#6c63ff', fontSize: 14 }}><BotIcon sx={{ fontSize: 16 }} /></Avatar>
      <Paper elevation={0} sx={{ px: 1.5, py: 1.2, bgcolor: '#f0edff', borderRadius: '4px 14px 14px 14px', border: '1px solid #e0d9ff' }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {[0, 0.2, 0.4].map((delay, i) => (
            <Box key={i} sx={{
              width: 7, height: 7, borderRadius: '50%', bgcolor: '#6c63ff',
              animation: 'botBounce 1.2s infinite', animationDelay: `${delay}s`,
              '@keyframes botBounce': {
                '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.5 },
                '40%': { transform: 'scale(1)', opacity: 1 },
              },
            }} />
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN WIDGET
// ─────────────────────────────────────────────────────────────
export default function AiBotWidget() {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showAllActions, setShowAllActions] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      from: 'bot',
      text: 'مرحباً! أنا مساعدك الذكي لنظام إدارة المدرسة.\n\nيمكنني التنقل بين جميع الصفحات، عرض الإحصائيات، وتنفيذ الإجراءات السريعة.',
      suggestions: ['إحصائيات سريعة', 'قائمة الطلاب', 'الصفحة المالية', 'ماذا تستطيع؟'],
    },
  ]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, typing, open, scrollToBottom]);
  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 120); }
  }, [open]);

  // When user navigates to a new page, offer context actions
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      const ctx = Object.entries(PAGE_CONTEXT).find(([k]) => location.pathname.startsWith(k));
      if (ctx && open) {
        const [, { label, suggestions }] = ctx;
        pushBotMsg({ text: `أنت الآن في صفحة "${label}". هل تريد:`, suggestions });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavigate = useCallback((target) => { navigate(target); setOpen(false); }, [navigate]);

  const pushBotMsg = useCallback((response) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), from: 'bot', text: response.text, action: response.action, target: response.target, suggestions: response.suggestions, stats: response.stats },
    ]);
    if (!open) setUnread((n) => n + 1);
  }, [open]);

  // Fetch all system stats in parallel
  const fetchAllStats = useCallback(async () => {
    const endpoints = [
      '/api/students/dashboard/quick-stats/',
      '/api/employees/dashboard-stats/',
      '/api/payments/dashboard-stats/',
    ];
    const labels = ['الطلاب', 'الموظفون', 'المالية'];
    const results = await Promise.allSettled(endpoints.map((e) => axiosInstance.get(e)));
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        const stats = formatStats(endpoints[i], r.value.data);
        if (typeof stats === 'object') {
          pushBotMsg({ text: '', stats });
        }
      }
    });
  }, [pushBotMsg]);

  const processInput = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      setMessages((prev) => [...prev, { id: Date.now(), from: 'user', text: trimmed }]);
      setInput('');
      setTyping(true);

      // Simulate thinking delay
      await new Promise((r) => setTimeout(r, 600 + Math.random() * 400));
      setTyping(false);

      const result = detectIntent(trimmed);

      if (!result) {
        pushBotMsg({
          text: 'لم أفهم طلبك تماماً. هل تقصد أحد هذه الخيارات؟',
          suggestions: ['إحصائيات سريعة', 'قائمة الطلاب', 'إضافة طالب جديد', 'الصفحة المالية', 'ماذا تستطيع؟'],
        });
        return;
      }

      if (result.action === 'fetch_all') {
        pushBotMsg({ text: 'دعني أجلب الإحصائيات الشاملة للنظام...' });
        await fetchAllStats();
        pushBotMsg({ text: 'هذه نظرة عامة على النظام. هل تريد التعمق في أي قسم؟', suggestions: ['تفاصيل الطلاب', 'تفاصيل الموظفين', 'التفاصيل المالية'] });
        return;
      }

      if (result.action === 'fetch') {
        pushBotMsg({ text: result.text });
        try {
          const res = await axiosInstance.get(result.endpoint);
          const stats = formatStats(result.endpoint, res.data);
          if (typeof stats === 'object') {
            pushBotMsg({ text: 'إليك الإحصائيات:', stats, suggestions: ['عرض الصفحة الكاملة', 'إحصائيات أخرى'] });
          } else {
            pushBotMsg({ text: stats });
          }
        } catch {
          pushBotMsg({ text: 'تعذّر جلب البيانات حالياً. يمكنك الاطلاع عليها من الصفحة مباشرة.', suggestions: ['فتح الصفحة'] });
        }
        return;
      }

      if (result.action === 'navigate' && result.target) {
        pushBotMsg(result);
        setTimeout(() => handleNavigate(result.target), 900);
        return;
      }

      pushBotMsg(result);
    },
    [pushBotMsg, handleNavigate, fetchAllStats]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); processInput(input); }
  };

  // Current page label
  const currentPageCtx = Object.entries(PAGE_CONTEXT).find(([k]) => location.pathname.startsWith(k));
  const currentPageLabel = currentPageCtx ? currentPageCtx[1].label : null;

  const visibleActions = showAllActions ? QUICK_ACTIONS : QUICK_ACTIONS.slice(0, 6);

  return (
    <>
      {/* ── Floating Button ── */}
      <Tooltip title="المساعد الذكي" placement="right">
        <Badge badgeContent={unread} color="error" overlap="circular">
          <Fab
            onClick={() => { setOpen((v) => !v); setMinimized(false); }}
            sx={{
              position: 'fixed', bottom: 24, left: 24, zIndex: 1300,
              background: 'linear-gradient(135deg, #6c63ff 0%, #3f3d89 100%)',
              color: '#fff', width: 56, height: 56,
              boxShadow: '0 4px 20px rgba(108,99,255,0.5)',
              '&:hover': { background: 'linear-gradient(135deg, #7c74ff 0%, #4f4d99 100%)', transform: 'scale(1.08)' },
              transition: 'all 0.2s ease',
            }}
          >
            {open ? <CloseIcon /> : (
              <Box sx={{ position: 'relative', display: 'flex' }}>
                <BotIcon sx={{ fontSize: 26 }} />
                <SparkleIcon sx={{ position: 'absolute', top: -8, right: -8, fontSize: 14, color: '#ffd700' }} />
              </Box>
            )}
          </Fab>
        </Badge>
      </Tooltip>

      {/* ── Chat Panel ── */}
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 92, left: 24, zIndex: 1299,
            width: 360, borderRadius: 3, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            maxHeight: minimized ? 56 : 520,
            transition: 'max-height 0.3s ease',
            boxShadow: '0 8px 40px rgba(108,99,255,0.25)',
            border: '1px solid rgba(108,99,255,0.15)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #6c63ff 0%, #3f3d89 100%)',
              px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.5,
              cursor: 'pointer', flexShrink: 0,
            }}
            onClick={() => setMinimized((v) => !v)}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <BotIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                المساعد الذكي
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                {currentPageLabel ? `📍 ${currentPageLabel}` : '● متصل الآن'}
              </Typography>
            </Box>
            <Tooltip title="مسح المحادثة">
              <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMessages([{ id: Date.now(), from: 'bot', text: 'تمت إعادة تشغيل المحادثة. كيف يمكنني مساعدتك؟', suggestions: ['إحصائيات سريعة', 'قائمة الطلاب', 'الصفحة المالية'] }]);
                }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5 }}
              onClick={(e) => { e.stopPropagation(); setMinimized((v) => !v); }}>
              <MinimizeIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5 }}
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {!minimized && (
            <>
              {/* Quick Actions */}
              <Box sx={{ px: 1.5, pt: 1, pb: 0.5, bgcolor: '#fafafa', borderBottom: '1px solid #f0edff', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {visibleActions.map((a) => (
                    <Chip key={a.label} label={`${a.icon} ${a.label}`} size="small"
                      onClick={() => handleNavigate(a.target)}
                      sx={{ fontSize: 11, cursor: 'pointer', bgcolor: '#fff', border: '1px solid #e0d9ff', '&:hover': { bgcolor: '#f0edff' } }} />
                  ))}
                  <Chip
                    label={showAllActions ? '▲ أقل' : `+${QUICK_ACTIONS.length - 6} المزيد`}
                    size="small"
                    onClick={() => setShowAllActions((v) => !v)}
                    sx={{ fontSize: 11, cursor: 'pointer', bgcolor: '#ede9ff', color: '#6c63ff', border: '1px solid #6c63ff' }}
                  />
                </Box>
              </Box>

              {/* Messages */}
              <Box sx={{
                flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, bgcolor: '#fff',
                display: 'flex', flexDirection: 'column', direction: 'rtl', minHeight: 0,
              }}>
                {messages.map((msg) =>
                  msg.from === 'bot'
                    ? <BotMessage key={msg.id} msg={msg} onSuggestion={processInput} onNavigate={handleNavigate} />
                    : <UserMessage key={msg.id} msg={msg} />
                )}
                {typing && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </Box>

              <Divider />

              {/* Input */}
              <Box sx={{ px: 1.5, py: 1, bgcolor: '#fff', display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth size="small"
                  placeholder="اكتب رسالتك هنا..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  multiline maxRows={3}
                  sx={{
                    direction: 'rtl',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3, fontSize: 13,
                      '&.Mui-focused fieldset': { borderColor: '#6c63ff' },
                    },
                  }}
                />
                <IconButton
                  onClick={() => processInput(input)}
                  disabled={!input.trim() || typing}
                  sx={{
                    bgcolor: input.trim() && !typing ? '#6c63ff' : '#e0e0e0',
                    color: '#fff', width: 36, height: 36, flexShrink: 0,
                    '&:hover': { bgcolor: input.trim() ? '#5a52cc' : '#e0e0e0' },
                    '&:disabled': { color: '#aaa' },
                    transition: 'all 0.2s',
                  }}
                >
                  {typing
                    ? <CircularProgress size={16} sx={{ color: '#aaa' }} />
                    : <SendIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />
                  }
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      )}
    </>
  );
}
